import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import React from "react";

import { RelodeTheme } from "..";
import { ExpandMoreIcon } from "../../assets/icons";
import { PanelProps } from "./panel";

const useStyles = makeStyles((theme: RelodeTheme) =>
    createStyles({
        root: {
            width: "100%",
        },
        titleContainer: {
            display: "flex",
            columnGap: "4px",
            alignItems: "center",
        },
    })
);

export interface ExpansionPanelProps {
    id: string;
    children: any | any[];
    initialPanelToExpand?: string;
    expansionMode?: "single" | "multiple";
}

export const ExpansionPanels = (props: ExpansionPanelProps) => {
    const classes = useStyles({});

    const { expansionMode = "single", children, initialPanelToExpand } = props;
    const [expandedPanels, setPanels] = React.useState<any>(() =>
        !!initialPanelToExpand ? { [initialPanelToExpand]: true } : {}
    );

    const handleChange = (panel: React.ReactElement<PanelProps>) => (
        event: React.ChangeEvent<{}>,
        isExpanded: boolean
    ) => {
        if (!panel) return;

        if (expansionMode === "multiple") {
            // update our panel and ignore others
            setPanels({ ...expandedPanels, [panel.props.id]: isExpanded });
        } else {
            // collapse all then set ours to the requested state
            const updatedPanels = {};
            Object.keys(expandedPanels).forEach(
                key => (updatedPanels[key] = false)
            );
            setPanels({ ...updatedPanels, [panel.props.id]: isExpanded });
        }

        // let others know about this change
        if (!!panel.props.onChange) {
            panel.props.onChange(panel, isExpanded);
        }
    };

    const childrenArray = Array.isArray(children)
        ? props.children
        : [props.children];

    const renderChildren = () =>
        React.Children.map(
            childrenArray,
            (panel: React.ReactElement<PanelProps>) => {
                if (!panel || panel.props.disabled) return null;
                const isExpanded =
                    panel.props.expanded ??
                    expandedPanels[panel.props.id] ??
                    false;
                return (
                    <Accordion
                        disabled={panel.props.disabled}
                        expanded={isExpanded}
                        onChange={handleChange(panel)}
                    >
                        <AccordionSummary
                            expandIcon={
                                panel.props?.hideExpandIcon ? null : (
                                    <ExpandMoreIcon color="inherit" />
                                )
                            }
                            id={panel.props.id}
                            IconButtonProps={{ color: "primary" }}
                            className={
                                isExpanded &&
                                panel.props?.customExpandedClassName
                                    ? panel.props.customExpandedClassName
                                    : null
                            }
                        >
                            {isExpanded && panel.props?.customExpandedHeader
                                ? panel.props.customExpandedHeader
                                : panel.props.heading}
                        </AccordionSummary>
                        <AccordionDetails
                            className={
                                panel.props?.customExpandedClassName
                                    ? panel.props.customExpandedClassName
                                    : null
                            }
                        >
                            {isExpanded ? panel.props.children : null}
                        </AccordionDetails>
                    </Accordion>
                );
            }
        );

    return <div className={classes.root}>{renderChildren()}</div>;
};
